import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, Plus, Ban, RotateCcw, Trash2, Lock, LockOpen, ArrowRight, Play, Pencil, Loader2 } from 'lucide-react';
import type { RunStatus, RunSummary } from '@lyra/shared';
import { DocTop } from '@/components/shell/DocTop';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { StatusLabel, type StatusTone } from '@/components/ui/StatusLabel';
import { formatCurrencyEurRo, formatDateRo, formatNumberRo } from '@/lib/format';
import { useWallet } from '@/lib/walletHooks';
import { useMe } from '@/lib/useMe';
import { canSeeBilling } from '@/lib/permissions';
import { useCancelRun, useDeleteRun, useFinalizeRun, useRenameRun, useRestartRunWithInstance, useRuns } from './api/runHooks';
import { groupRunsByDate } from './lib/rows';
import { runActualCostEur } from './lib/timing';

type StatusFilterValue = 'all' | RunStatus;

/**
 * Single source of truth for both the row status dot+label and the filter-tab
 * label — Rule Zero requires the same Romanian label everywhere a status is
 * named. Labels are v1's exact strings (see `getStatusMeta` in
 * `app/src/components/JobsView.tsx`) — `RunStatus` has no "interrupted"
 * value, so a queued run is labeled „În coadă" (in the queue), not invented
 * as "interrupted".
 */
const STATUS_META: Record<RunStatus, { label: string; dotClass: string }> = {
  queued: { label: 'În coadă', dotClass: 'bg-warn' },
  processing: { label: 'În curs', dotClass: 'bg-teal motion-safe:animate-pulse' },
  completed: { label: 'Finalizat', dotClass: 'bg-ok' },
  failed: { label: 'Eșuat', dotClass: 'bg-bad' },
  cancelled: { label: 'Anulat', dotClass: 'bg-mute' },
};

const STATUS_FILTERS: Array<{ value: StatusFilterValue; label: string }> = [
  { value: 'all', label: 'Toate' },
  { value: 'processing', label: STATUS_META.processing.label },
  { value: 'queued', label: STATUS_META.queued.label },
  { value: 'completed', label: STATUS_META.completed.label },
  { value: 'cancelled', label: STATUS_META.cancelled.label },
  { value: 'failed', label: STATUS_META.failed.label },
];

/** `STATUS_META`'s dot color, mapped onto `StatusLabel`'s closed tone
 *  vocabulary for the row-level status cell — same idiom as
 *  `ResultsTable.tsx`'s `STATUS_TONE` (2026-08-19 remediation Slice D,
 *  Task 3 fix round: this used to be a `Pill`-variant map named
 *  `STATUS_PILL_VARIANT`, rendered as a wash-filled chip; row status is now
 *  `StatusLabel`'s dot+label form, like everywhere else). */
const STATUS_TONE: Record<RunStatus, StatusTone> = {
  queued: 'warn',
  processing: 'teal',
  completed: 'ok',
  failed: 'bad',
  cancelled: 'mute',
};

/**
 * The status shown for a run. `RunStatus === 'completed'` means classification
 * finished — NOT that the mapare is done: it still has to be validated and then
 * finalized (locked). So a completed run reads „Finalizat" (green) only once it
 * is actually finalized (`finalized_at`); until then it is still in review and
 * reads „În validare" (warn). The green „Finalizat" on an unvalidated run was
 * the misleading part.
 */
function runStatusDisplay(run: RunSummary): { label: string; tone: StatusTone; pulse?: boolean } {
  if (run.status === 'completed' && !run.finalized_at) {
    return { label: 'În validare', tone: 'warn' };
  }
  // `pulse` carries STATUS_META's „live" dot cue (the filter tabs already
  // render it via `dotClass`'s literal `motion-safe:animate-pulse` suffix)
  // through to the row's own status now that it renders as a dot too, not a
  // plain chip that had no dot to carry it on.
  return {
    label: STATUS_META[run.status].label,
    tone: STATUS_TONE[run.status],
    pulse: run.status === 'processing',
  };
}

/** A run still shown as "actively in flight" — resumes automatically, can be cancelled. */
function isInFlight(status: RunStatus): boolean {
  return status === 'processing' || status === 'queued';
}

const linkBtnBase = 'text-[13.5px] font-medium hover:underline underline-offset-2 disabled:opacity-50 disabled:pointer-events-none disabled:no-underline';
const iconActionBase =
  'flex size-7 shrink-0 items-center justify-center rounded transition-colors disabled:opacity-40 disabled:pointer-events-none';

/**
 * Actual per-run cost = duration (started_at → finished_at) × org's hourly
 * rate. `RunSummary` is snake_case (server run-shape); `runActualCostEur`
 * (Task 3, `./lib/timing`) takes the camelCase `{startedAt, finishedAt}`
 * shape shared with `RunDetail`, so the snake fields are adapted at the call
 * site rather than adding a second overload. „—" for runs that haven't
 * finished yet (no `finished_at`) and for a computed cost of 0 (unstarted /
 * clock-skew / genuinely free) — a bare „0,00 €" would read as a priced,
 * completed run rather than "no cost data".
 */
function formatRunCost(run: RunSummary, hourlyPriceEur: number): string {
  if (!run.finished_at) return '—';
  const cost = runActualCostEur({ startedAt: run.started_at, finishedAt: run.finished_at }, hourlyPriceEur);
  return cost === 0 ? '—' : formatCurrencyEurRo(cost);
}

/**
 * Progress cell that keeps a constant width as it polls. A run's total is
 * fixed, so the widest „procesate/total" ever gets is „total/total" — we
 * reserve exactly that with an invisible twin and overlay the live value
 * right-aligned on top. Without it the auto-layout table reflowed (and the
 * whole grid visibly flickered) every 2s as `processed_rows` gained a digit.
 */
function ProgressCell({ processed, total, title }: { processed: number; total: number; title?: string }) {
  return (
    <span className="inline-grid tabular-nums" title={title}>
      <span className="invisible col-start-1 row-start-1 whitespace-nowrap" aria-hidden>
        {formatNumberRo(total)}/{formatNumberRo(total)}
      </span>
      <span className="col-start-1 row-start-1 whitespace-nowrap text-right">
        {formatNumberRo(processed)}/{formatNumberRo(total)}
      </span>
    </span>
  );
}

export function MapariListPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilterValue>('all');
  const [search, setSearch] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingFilename, setEditingFilename] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<number | null>(null);
  // Reflect only a *manual* refresh in the button — the list background-polls
  // every 2s while a run is active, and keying the button off that `isFetching`
  // pulse made its label flip twice a second (C-flicker).
  const [manualRefreshing, setManualRefreshing] = useState(false);

  const { data: runs, isPending, isError, refetch } = useRuns(statusFilter);

  async function handleRefresh() {
    setManualRefreshing(true);
    try {
      await refetch();
    } finally {
      setManualRefreshing(false);
    }
  }
  const { data: wallet, isPending: walletPending } = useWallet();
  const { data: me } = useMe();
  // Billing-privacy: members never see the Cost column (no header, no cells,
  // no empty „—" artifact) — only owners/admins/platform admins do.
  const billing = canSeeBilling(me);
  const cancelRun = useCancelRun();
  const restartRun = useRestartRunWithInstance();
  const deleteRun = useDeleteRun();
  const renameRun = useRenameRun();
  const finalizeRun = useFinalizeRun();

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = runs ?? [];
    if (!term) return list;
    return list.filter((r) => r.filename.toLowerCase().includes(term));
  }, [runs, search]);

  const grouped = useMemo(() => groupRunsByDate(filtered), [filtered]);

  const hasAnyRuns = (runs ?? []).length > 0;

  function startRename(run: RunSummary) {
    setEditingId(run.id);
    setEditingFilename(run.filename);
  }

  return (
    <>
      <DocTop />
      <div className="mt-8">
        <div className="flex items-baseline justify-between gap-4">
          <h1 className="text-[32px] font-[650] text-ink">Mapări</h1>
          <div className="flex items-center gap-4">
            {/* C2 (parity — v1 „Reîmprospătare"): the list only auto-polls while a
                run is active, so give an explicit refresh to catch a run a
                colleague just started on a quiet list. */}
            <button
              type="button"
              onClick={handleRefresh}
              disabled={manualRefreshing}
              aria-label={manualRefreshing ? 'Se reîmprospătează…' : 'Reîmprospătează'}
              title="Reîmprospătează"
              className="flex size-8 items-center justify-center rounded-md text-ink-3 hover:bg-wash hover:text-ink-2 disabled:opacity-50"
            >
              {/* Icon-only (universally understood + spin communicates the fetch),
                  so no text-width flicker to reserve against; the manual-only
                  handler still keeps it off the 2s background poll. */}
              <RefreshCw className={`size-4 ${manualRefreshing ? 'motion-safe:animate-spin' : ''}`} />
            </button>
            <Button onClick={() => navigate('/mapari/noua')}>
              <Plus />
              Mapare nouă
            </Button>
          </div>
        </div>
        <div className="mt-1 border-b-2 border-ink pb-3" />

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-1.5">
            {STATUS_FILTERS.map((f) => {
              const active = statusFilter === f.value;
              const dotClass = f.value === 'all' ? 'bg-ink-3' : STATUS_META[f.value].dotClass;
              return (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => setStatusFilter(f.value)}
                  aria-pressed={active}
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-[13.5px] font-medium transition-colors ${
                    active ? 'border-rule bg-wash text-ink' : 'border-transparent text-ink-2 hover:bg-wash'
                  }`}
                >
                  <span className={`size-2 shrink-0 rounded-full ${dotClass}`} aria-hidden />
                  {f.label}
                </button>
              );
            })}
          </div>
          <Input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Caută după fișier…"
            className="h-9 w-64"
            aria-label="Caută după fișier"
          />
        </div>

        <div className="mt-6">
          {isError && <p className="text-[13.5px] text-bad">Nu s-au putut încărca mapările.</p>}

          {!isError && isPending && (
            <div>
              <div className="h-9 border-b border-rule" aria-hidden />
              <div className="divide-y divide-rule-soft">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="px-4 py-3">
                    <div className="h-4 w-full max-w-md animate-pulse rounded-md bg-wash" aria-hidden />
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isError && !isPending && !hasAnyRuns && (
            <p className="py-10 text-center text-[13.5px] text-ink-3">
              Nicio mapare încă. Începeți cu «Mapare nouă».
            </p>
          )}

          {!isError && !isPending && hasAnyRuns && filtered.length === 0 && (
            <p className="py-10 text-center text-[13.5px] text-ink-3">Niciun rezultat pentru filtrele curente.</p>
          )}

          {!isError && !isPending && filtered.length > 0 && (
            <div className="overflow-x-auto">
            <div className="min-w-[760px] md:min-w-0">
            <table className="w-full border-separate border-spacing-0 text-[13.5px]">
              <thead>
                <tr className="border-b border-rule text-left text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-3 [&>th]:px-4 [&>th]:py-2.5">
                  <th>Fișier</th>
                  <th>Dată</th>
                  <th className="text-right">Rânduri</th>
                  <th className="text-right">Progres</th>
                  {billing && <th className="text-right">Cost</th>}
                  <th>Status</th>
                  <th className="text-right">Acțiuni</th>
                </tr>
              </thead>
              {grouped.map((group) => (
                <tbody key={group.label}>
                  <tr>
                    <td colSpan={billing ? 7 : 6} className="border-t border-rule px-4 py-1.5 text-[11.5px] font-bold uppercase tracking-[0.05em] text-ink-3">
                      {group.label}
                    </td>
                  </tr>
                  {group.runs.map((run) => {
                    const statusDisplay = runStatusDisplay(run);
                    const inFlight = isInFlight(run.status);
                    const showProgress = run.processed_rows < run.total_rows;
                    const isEditing = editingId === run.id;
                    const isConfirmingDelete = confirmDeleteId === run.id;
                    const isConfirmingCancel = confirmCancelId === run.id;
                    const cancelPending = cancelRun.isPending && cancelRun.variables === run.id;
                    const restartPending = restartRun.isPending && restartRun.variables === run.id;
                    const deletePending = deleteRun.isPending && deleteRun.variables === run.id;
                    const finalizePending = finalizeRun.isPending && finalizeRun.variables?.id === run.id;
                    const isFinalized = Boolean(run.finalized_at);

                    return (
                      <tr key={run.id} className="border-t border-rule-soft align-top hover:bg-wash [&>td]:px-4 [&>td]:py-2">
                        <td>
                          {isEditing ? (
                            <Input
                              autoFocus
                              aria-label="Redenumește maparea"
                              value={editingFilename}
                              onChange={(e) => setEditingFilename(e.target.value)}
                              onBlur={() => {
                                const next = editingFilename.trim();
                                // Only PATCH on a real change — blurring an unedited name
                                // must not fire a no-op request + false „redenumită" toast.
                                if (next && next !== run.filename) renameRun.mutate({ id: run.id, filename: next });
                                setEditingId(null);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') e.currentTarget.blur();
                                if (e.key === 'Escape') setEditingId(null);
                              }}
                              className="h-7 w-full"
                            />
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                onClick={() => navigate(`/mapari/${run.id}`)}
                                className="max-w-[280px] truncate text-left text-[13.5px] text-ink hover:underline"
                                title={`Deschide ${run.filename}`}
                              >
                                {run.filename}
                              </button>
                              <button
                                type="button"
                                onClick={() => startRename(run)}
                                title="Redenumește"
                                aria-label="Redenumește maparea"
                                className="shrink-0 text-ink-3 transition-colors hover:text-ink"
                              >
                                <Pencil className="size-3.5" />
                              </button>
                            </div>
                          )}
                          {run.status === 'failed' && run.error_message && (
                            <p className="mt-0.5 max-w-[280px] truncate text-[12.5px] text-ink-3" title={run.error_message}>
                              {run.error_message}
                            </p>
                          )}
                          {run.status === 'processing' && (run.current_input_code || run.current_input_name) && (
                            // Fixed width (not max-w): the current code/name changes every 2s poll;
                            // a variable width would resize the Fișier column and reflow the whole table.
                            <p className="mt-0.5 w-[280px] max-w-full truncate text-[12.5px] text-ink-3">
                              Curent: {run.current_input_code || '—'} {run.current_input_name ? `· ${run.current_input_name}` : ''}
                            </p>
                          )}
                        </td>
                        <td className="whitespace-nowrap tabular-nums text-ink-2">{formatDateRo(new Date(run.created_at))}</td>
                        <td className="text-right tabular-nums text-ink-2">{formatNumberRo(run.total_rows)}</td>
                        <td className="text-right tabular-nums text-ink-3">
                          {showProgress ? (
                            <ProgressCell processed={run.processed_rows} total={run.total_rows} title="Procesate din total" />
                          ) : run.status === 'completed' && Number.isFinite(run.validated_count) ? (
                            // În validare / finalizat: how many rows the operator has
                            // validated out of the total (owner request). Guard against a
                            // stale server that predates validated_count — show nothing
                            // rather than „NaN/total".
                            <ProgressCell processed={run.validated_count} total={run.progress_total ?? run.total_rows} title="Validate din total" />
                          ) : (
                            ''
                          )}
                        </td>
                        {billing && (
                          <td className="whitespace-nowrap text-right tabular-nums text-ink-2">
                            {walletPending || !wallet ? '—' : formatRunCost(run, wallet.hourlyPriceEur)}
                          </td>
                        )}
                        <td>
                          <div className="flex flex-col items-start gap-1">
                            <StatusLabel tone={statusDisplay.tone} label={statusDisplay.label} pulse={statusDisplay.pulse} />
                            {isFinalized && (
                              <span className="inline-flex items-center gap-1.5 text-[12.5px] text-ink-3">
                                <span className="size-2 shrink-0 rounded-full bg-ink-3" aria-hidden />
                                Blocată pentru editare
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center justify-end gap-1">
                            {inFlight && (
                              isConfirmingCancel ? (
                                <span className="inline-flex items-center gap-2 whitespace-nowrap">
                                  <span className="text-[13.5px] text-ink-3">Sigur?</span>
                                  <button
                                    type="button"
                                    onClick={() => { cancelRun.mutate(run.id); setConfirmCancelId(null); }}
                                    disabled={cancelPending}
                                    className={`${linkBtnBase} text-bad`}
                                  >
                                    Da, anulează
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setConfirmCancelId(null)}
                                    className={`${linkBtnBase} text-ink-3 hover:text-ink-2`}
                                  >
                                    Nu
                                  </button>
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setConfirmCancelId(run.id)}
                                  disabled={cancelPending}
                                  title="Anulează"
                                  aria-label="Anulează maparea"
                                  className={`${iconActionBase} text-ink-3 hover:text-bad`}
                                >
                                  {cancelPending ? <Loader2 className="size-4 motion-safe:animate-spin" /> : <Ban className="size-4" />}
                                </button>
                              )
                            )}
                            {run.status === 'failed' && (
                              <button
                                type="button"
                                onClick={() => restartRun.run(run.id)}
                                disabled={restartPending}
                                title="Repornire"
                                aria-label="Repornește maparea"
                                className={`${iconActionBase} text-ink-3 hover:text-ink`}
                              >
                                {restartPending ? <Loader2 className="size-4 motion-safe:animate-spin" /> : <RotateCcw className="size-4" />}
                              </button>
                            )}
                            {(run.status === 'completed' || run.status === 'cancelled') && !isFinalized && (
                              isConfirmingDelete ? (
                                <span className="inline-flex items-center gap-2 whitespace-nowrap">
                                  <span className="text-[13.5px] text-ink-3">Sigur?</span>
                                  <button
                                    type="button"
                                    onClick={() => { deleteRun.mutate(run.id); setConfirmDeleteId(null); }}
                                    disabled={deletePending}
                                    className={`${linkBtnBase} text-bad`}
                                  >
                                    Da, șterge
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => setConfirmDeleteId(null)}
                                    className={`${linkBtnBase} text-ink-3 hover:text-ink-2`}
                                  >
                                    Nu
                                  </button>
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => setConfirmDeleteId(run.id)}
                                  title="Șterge"
                                  aria-label="Șterge maparea"
                                  className={`${iconActionBase} text-ink-3 hover:text-bad`}
                                >
                                  <Trash2 className="size-4" />
                                </button>
                              )
                            )}
                            {!inFlight && isFinalized && (
                              <button
                                type="button"
                                onClick={() => finalizeRun.mutate({ id: run.id, finalized: false })}
                                disabled={finalizePending}
                                title="Redeschide"
                                aria-label="Redeschide pentru editare"
                                className={`${iconActionBase} text-ink-3 hover:text-ink`}
                              >
                                {finalizePending ? <Loader2 className="size-4 motion-safe:animate-spin" /> : <LockOpen className="size-4" />}
                              </button>
                            )}
                            {!inFlight && !isFinalized && (
                              <button
                                type="button"
                                onClick={() => finalizeRun.mutate({ id: run.id, finalized: true })}
                                disabled={finalizePending}
                                title="Blochează editarea"
                                aria-label="Blochează editarea"
                                className={`${iconActionBase} text-ink-3 hover:text-ink`}
                              >
                                {finalizePending ? <Loader2 className="size-4 motion-safe:animate-spin" /> : <Lock className="size-4" />}
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => navigate(`/mapari/${run.id}`)}
                              title={inFlight ? 'Reia' : 'Deschide'}
                              aria-label={inFlight ? 'Reia maparea' : 'Deschide maparea'}
                              className={`${iconActionBase} text-teal hover:bg-teal-wash`}
                            >
                              {inFlight ? <Play className="size-4" /> : <ArrowRight className="size-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              ))}
            </table>
            </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
